/**
 * orcamento-modelos-store — Modelos de Orçamento (M2 · A7).
 *
 * A7: o modelo é uma entidade NOMEADA com tipo de projeto OPCIONAL. Hooks
 * TanStack Query pro CRUD de modelos (cards da aba "Modelos de orçamento") e
 * pros blocos + itens sugeridos de cada modelo. Cada mutação de bloco/item
 * devolve a lista completa de blocos do modelo, que vira o novo cache da query
 * daquele modelo. Gated no backend por ORCAMENTO_ENABLED.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type TipoBloco = 'fixo' | 'repetivel';

export interface ModeloItemSugerido {
  id: string;
  blocoId: string;
  nome: string;
  ordem: number;
  criadoEmIso: string;
}

export interface ModeloBloco {
  id: string;
  /** Dono do bloco (A7). */
  modeloId: string | null;
  /** Legado pré-369 (não usar como dono). */
  tipoId: string | null;
  nome: string;
  tipoBloco: TipoBloco;
  ordem: number;
  criadoEmIso: string;
  itens: ModeloItemSugerido[];
}

export interface OrcamentoModelo {
  id: string;
  nome: string;
  tipoId: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string | null;
}

/** Resumo pros cards da aba (contagens + nomes dos blocos). */
export interface OrcamentoModeloResumo extends OrcamentoModelo {
  tipoNome: string | null;
  blocosCount: number;
  itensCount: number;
  blocoNomes: string[];
}

interface BlocosResp {
  blocos: ModeloBloco[];
}

const KEY = ['orcamento-modelos'] as const;
const modelosKey = [...KEY, 'lista'] as const;
const blocosKey = (modeloId: string | null) => [...KEY, 'modelo', modeloId, 'blocos'] as const;

/** GET /modelos — todos os modelos (cards). */
export function useModelos() {
  return useQuery({
    queryKey: modelosKey,
    queryFn: () => api.get<{ modelos: OrcamentoModeloResumo[] }>('/api/v1/orcamento-modelos/modelos'),
    enabled: hasToken(),
    select: (r) => r.modelos,
  });
}

/** GET /modelos/:modeloId/blocos — blocos (com itens sugeridos) de um modelo. */
export function useModeloBlocos(modeloId: string | null) {
  return useQuery({
    queryKey: blocosKey(modeloId),
    queryFn: () => api.get<BlocosResp>(`/api/v1/orcamento-modelos/modelos/${modeloId}/blocos`),
    enabled: hasToken() && !!modeloId,
  });
}

/** CRUD de modelos (admin). */
export function useModelosActions() {
  const qc = useQueryClient();
  const invalidateLista = () => qc.invalidateQueries({ queryKey: modelosKey });

  const createModelo = useMutation({
    mutationFn: (input: { nome: string; tipoId?: string | null }) =>
      api.post<{ modelo: OrcamentoModelo }>('/api/v1/orcamento-modelos/modelos', input),
    onSuccess: invalidateLista,
  });
  const updateModelo = useMutation({
    mutationFn: ({ modeloId, patch }: { modeloId: string; patch: { nome?: string; tipoId?: string | null } }) =>
      api.patch<{ modelo: OrcamentoModelo }>(`/api/v1/orcamento-modelos/modelos/${modeloId}`, patch),
    onSuccess: invalidateLista,
  });
  const removeModelo = useMutation({
    mutationFn: (modeloId: string) => api.delete<{ ok: true }>(`/api/v1/orcamento-modelos/modelos/${modeloId}`),
    onSuccess: invalidateLista,
  });

  return { createModelo, updateModelo, removeModelo };
}

/**
 * Mutations admin de blocos + itens de UM modelo. Cada uma escreve a lista
 * nova no cache do modelo e invalida a lista de cards (contagens mudam).
 */
export function useModeloBlocoActions(modeloId: string) {
  const qc = useQueryClient();
  const writeCache = (resp: BlocosResp) => {
    qc.setQueryData(blocosKey(modeloId), { blocos: resp.blocos });
    qc.invalidateQueries({ queryKey: modelosKey });
  };

  const addBloco = useMutation({
    mutationFn: (input: { nome: string; tipoBloco?: TipoBloco }) =>
      api.post<BlocosResp & { bloco: ModeloBloco }>(`/api/v1/orcamento-modelos/modelos/${modeloId}/blocos`, input),
    onSuccess: writeCache,
  });
  const updateBloco = useMutation({
    mutationFn: ({ blocoId, patch }: { blocoId: string; patch: { nome?: string; tipoBloco?: TipoBloco } }) =>
      api.patch<BlocosResp>(`/api/v1/orcamento-modelos/blocos/${blocoId}`, patch),
    onSuccess: writeCache,
  });
  const removeBloco = useMutation({
    mutationFn: (blocoId: string) => api.delete<BlocosResp>(`/api/v1/orcamento-modelos/blocos/${blocoId}`),
    onSuccess: writeCache,
  });
  const reorderBlocos = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put<BlocosResp>(`/api/v1/orcamento-modelos/modelos/${modeloId}/blocos/reorder`, { orderedIds }),
    onSuccess: writeCache,
  });

  const addItem = useMutation({
    mutationFn: ({ blocoId, nome }: { blocoId: string; nome: string }) =>
      api.post<BlocosResp>(`/api/v1/orcamento-modelos/blocos/${blocoId}/itens`, { nome }),
    onSuccess: writeCache,
  });
  const updateItem = useMutation({
    mutationFn: ({ itemId, nome }: { itemId: string; nome: string }) =>
      api.patch<BlocosResp>(`/api/v1/orcamento-modelos/itens/${itemId}`, { nome }),
    onSuccess: writeCache,
  });
  const removeItem = useMutation({
    mutationFn: (itemId: string) => api.delete<BlocosResp>(`/api/v1/orcamento-modelos/itens/${itemId}`),
    onSuccess: writeCache,
  });

  return { addBloco, updateBloco, removeBloco, reorderBlocos, addItem, updateItem, removeItem };
}
